import React, { useEffect, useState } from "react";
import {
  createDocument,
  updateDocument,
  uploadAsset,
} from "../services/scenarioAdminService";

export interface DocumentDraft {
  id?: string;
  documentKey?: string;
  name: string;
  description: string;
  instructions?: string;
  objectKey?: string;
  importedObjectKey?: string;
  sourceUrl?: string;
  importedFile?: File;
  // Local-only — the filename of the staged file, to show the user what they'll
  // upload. Cleared once the upload succeeds.
  stagedFilename?: string;
}

interface DocumentFormModalProps {
  open: boolean;
  document: DocumentDraft | null;
  scenarioId: string;
  getToken: () => string | undefined;
  onSave: (doc: DocumentDraft) => void;
  onClose: () => void;
}

const EMPTY_DOC: DocumentDraft = {
  name: "",
  description: "",
  instructions: "",
};

/**
 * Create/edit a scenario document.
 *
 * scenarioDocuments.object_key is NOT NULL in the DB. The server's signing
 * endpoint supports a "_pending" key shape for documents without a documentId
 * yet (see scenarios.service.ts), so the upload happens BEFORE the
 * createDocument call. For edits, a fresh file upload regenerates a normal
 * server-shaped key under the existing documentId.
 */
const DocumentFormModal: React.FC<DocumentFormModalProps> = ({
  open,
  document,
  scenarioId,
  getToken,
  onSave,
  onClose,
}) => {
  const [form, setForm] = useState<DocumentDraft>(EMPTY_DOC);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(document ?? EMPTY_DOC);
      setPendingFile(document?.importedFile ?? null);
      setError(null);
    }
  }, [open, document]);

  if (!open) return null;

  const isEdit = !!form.id;

  const update = <K extends keyof DocumentDraft>(
    k: K,
    v: DocumentDraft[K],
  ) => setForm((f) => ({ ...f, [k]: v }));

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError("File exceeds 10 MB limit.");
      return;
    }
    setPendingFile(file);
    update("stagedFilename", file.name);
    setError(null);
  };

  const validate = (): string | null => {
    if (!form.name.trim()) return "Name is required.";
    if (!form.description.trim()) return "Description is required.";
    if (!isEdit && !pendingFile) return "A file is required for a new document.";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const token = getToken();

      let objectKey = form.objectKey;

      if (pendingFile) {
        // For new docs, the server signs a key under
        // scenarios/<id>/documents/_pending/. For edits with an existing
        // documentId, the server signs under
        // scenarios/<id>/documents/<docId>/.
        objectKey = await uploadAsset(
          "document",
          { scenarioId, documentId: form.id },
          pendingFile,
          token,
        );
      }

      if (!objectKey) {
        throw new Error("Document is missing an uploaded file.");
      }

      let docId = form.id;
      if (!isEdit) {
        const created = await createDocument(
          scenarioId,
          {
            name: form.name.trim(),
            description: form.description.trim(),
            instructions: form.instructions?.trim() || undefined,
            objectKey,
          },
          token,
        );
        docId = created.id;
      } else {
        await updateDocument(
          scenarioId,
          docId!,
          {
            name: form.name.trim(),
            description: form.description.trim(),
            instructions: form.instructions?.trim() || undefined,
            objectKey,
          },
          token,
        );
      }

      onSave({ ...form, id: docId, objectKey, stagedFilename: undefined });
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to save document.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-panel modal-panel--md">
        <div className="modal-header">
          <h3 className="modal-title">
            {isEdit ? "Edit Document" : "Add Document"}
          </h3>
          <button
            onClick={onClose}
            disabled={busy}
            className="icon-action"
            aria-label="Close"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="modal-body">
          {/* Name */}
          <div>
            <label className="form-label">
              Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="form-control"
            />
          </div>

          {/* Description */}
          <div>
            <label className="form-label">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              rows={3}
              className="form-control"
            />
          </div>

          {/* Instructions (LLM override) */}
          <div>
            <label className="form-label">
              Instructions
            </label>
            <textarea
              value={form.instructions ?? ""}
              onChange={(e) => update("instructions", e.target.value)}
              rows={3}
              placeholder="Optional LLM-only override. Users never see this."
              className="form-control form-control--mono"
            />
            <p className="form-help">
              LLM-only override; users never see this.
            </p>
          </div>

          {/* File */}
          <div>
            <label className="form-label">
              File {isEdit ? "(replace)" : "(required)"}
            </label>
            <input
              type="file"
              onChange={handleFileSelect}
              className="form-file-input"
            />
            <p className="form-help">Max 10 MB.</p>
            {form.stagedFilename && (
              <p className="text-xs text-slate-400 mt-1">
                Pending: {form.stagedFilename}
              </p>
            )}
            {isEdit && form.objectKey && !pendingFile && (
              <p className="form-help italic">
                Current file will be retained if no new file is selected.
              </p>
            )}
          </div>

          {error && (
            <div className="form-error">
              {error}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="button-secondary"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy}
            className="button-primary"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentFormModal;
